import { t } from "i18next";

import {
  configuredDefaultCountry,
  governmentOrganizationLabelKey,
} from "@/common/defaultCountry";

import { RoleRead } from "@/types/emr/role/role";
import { UserReadMinimal } from "@/types/user/user";

type org_type = "team" | "govt" | "role" | "product_supplier" | "other";

export enum OrgType {
  TEAM = "team",
  GOVT = "govt",
  ROLE = "role",
  PRODUCT_SUPPLIER = "product_supplier",
  OTHER = "other",
}

export type Metadata = {
  govt_org_children_type?: string;
  govt_org_type?: string;
};

export interface OrganizationParent {
  id: string;
  name: string;
  description?: string;
  metadata: Metadata | null;
  org_type: org_type;
  level_cache: number;
  parent?: OrganizationParent;
}

export interface OrganizationUpdate {
  name?: string;
  description?: string;
  org_type?: OrgType;
  parent_id?: string;
}
export interface Organization {
  id: string;
  name: string;
  description?: string;
  org_type: org_type;
  level_cache: number;
  has_children: boolean;
  active: boolean;
  parent?: OrganizationParent;
  created_at: string;
  updated_at: string;
  metadata: Metadata | null;
  permissions: string[];
  managing_organizations?: OrganizationParent[];
}

export interface OrganizationCreate {
  name: string;
  description?: string;
  org_type: OrgType;
  parent_id?: string;
}

export interface OrganizationUserRole {
  id: string;
  user: UserReadMinimal;
  role: RoleRead;
}

const defaultCountryCode = configuredDefaultCountry(
  import.meta.env?.REACT_DEFAULT_COUNTRY,
).code;

export const getOrgLabel = (
  orgType: string | undefined,
  metadata?: Metadata | null,
  level = 0,
  countryCode = defaultCountryCode,
) => {
  if (orgType === "govt") {
    const type =
      typeof metadata?.govt_org_type === "string"
        ? metadata.govt_org_type
        : "default";
    return t(governmentOrganizationLabelKey(countryCode, type, level)) || "";
  }
  return typeof orgType === "string" ? orgType : "";
};

export const renderGeoOrganizations = (
  geoOrg: unknown,
  countryCode = defaultCountryCode,
) => {
  const details: { label: string; value: string }[] = [];
  const seen = new Set<object>();
  const formatValue = (name: string, label: string) => {
    return label && name.endsWith(label)
      ? name.slice(0, -label.length).trim() || name
      : name;
  };
  let current: unknown = geoOrg;
  while (
    current &&
    typeof current === "object" &&
    !Array.isArray(current) &&
    !seen.has(current) &&
    seen.size < 64
  ) {
    seen.add(current);
    const org = current as Record<string, unknown>;
    if (typeof org.name === "string" && org.name.trim()) {
      const metadata =
        org.metadata &&
        typeof org.metadata === "object" &&
        !Array.isArray(org.metadata)
          ? (org.metadata as Metadata)
          : null;
      const level =
        typeof org.level_cache === "number" &&
        Number.isInteger(org.level_cache) &&
        org.level_cache >= 0
          ? org.level_cache
          : 0;
      const label = getOrgLabel(
        typeof org.org_type === "string" ? org.org_type : undefined,
        metadata,
        level,
        countryCode,
      );
      details.push({ label, value: formatValue(org.name, label) });
    }
    current = org.parent;
  }
  return details;
};
